// ============================================================================
// NOTICE RETURN STATUS SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { noticeReturnStatusEndpoints, NOTICE_RETURN_STATUS_BASE_PATH } from '../endpoints/noticeReturnStatus.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  NoticeReturnStatus,
  NoticeReturnStatusListItem,
  CreateNoticeReturnStatusRequest,
  UpdateNoticeReturnStatusRequest,
  NoticeReturnStatusSearchRequest,
} from '../types/noticeReturnStatus.types';

class NoticeReturnStatusService {
  async getAll(params?: NoticeReturnStatusSearchRequest): Promise<PaginatedResponse<NoticeReturnStatusListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(NOTICE_RETURN_STATUS_BASE_PATH, params)
      : noticeReturnStatusEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<NoticeReturnStatus>> {
    const response = await get<ApiResponse<NoticeReturnStatus>>(noticeReturnStatusEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<NoticeReturnStatus>> {
    const response = await get<ApiResponse<NoticeReturnStatus>>(noticeReturnStatusEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateNoticeReturnStatusRequest): Promise<ApiResponse<NoticeReturnStatus>> {
    const response = await post<ApiResponse<NoticeReturnStatus>>(noticeReturnStatusEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateNoticeReturnStatusRequest): Promise<ApiResponse<NoticeReturnStatus>> {
    const response = await put<ApiResponse<NoticeReturnStatus>>(noticeReturnStatusEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(noticeReturnStatusEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${noticeReturnStatusEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : noticeReturnStatusEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const noticeReturnStatusService = new NoticeReturnStatusService();
export default noticeReturnStatusService;
